import { html, LitElement } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import type { ButtonColor, ButtonFontSize, ButtonHeight, ButtonTheme, ButtonType } from '../types';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonThemes, ButtonTypes } from '../types';
import { isElementOverflowed, OverflowDirection } from '../../../utils/htmlElement.utils';
import { GzTooltip } from '../../gzTooltip';

@customElement('gz-button')
export class GzButtonWebComponent extends LitElement {
  /**
   *  The default button type has background-color and white text
   */
  @property({ type: String, attribute: true, reflect: true })
  type: ButtonType = ButtonTypes.FULL;

  /**
   * The default button color theme is blue
   */
  @property({ type: String, attribute: true, reflect: true })
  color: ButtonColor = ButtonColors.PRIMARY;

  /**
   * The default button theme is light
   */
  @property({ type: String, attribute: true, reflect: true })
  theme: ButtonTheme = ButtonThemes.LIGHT;

  /**
   * The default button height is Medium (32px).
   */
  @property({ type: String, attribute: true, reflect: true })
  buttonHeight: ButtonHeight = ButtonHeights.MEDIUM;

  /**
   * The default button font size is 14px.
   */
  @property({ type: String, attribute: true, reflect: true })
  buttonFontSize: ButtonFontSize = ButtonFontSizes.MEDIUM;
  /**
   * Set this property to true for lowercase button text.
   */
  @property({ type: Boolean, reflect: true })
  lowercase = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @property({ type: Boolean, reflect: true })
  showSpinner = false;

  constructor() {
    super();
    this.addEventListener('click', this.clickHandler);
  }

  private clickHandler(e: Event) {
    if (this.disabled) {
      e.stopImmediatePropagation();
    }
  }

  static get styles() {
    return styles;
  }

  private renderButtonContent() {
    if (this.showSpinner) {
      return this.renderSpinner();
    }

    return this.renderSlot();
  }

  private renderSpinner() {
    return html` <gz-progress-spinner class="button-spinner" spinnerwidth="small"></gz-progress-spinner> `;
  }

  private renderSlot() {
    return html`
      <span part="button-text" class="button-text">
        <slot></slot>
      </span>
    `;
  }

  render() {
    return html`
      <button
        @mouseenter="${this.handleMouseEnter}"
        @mousemove="${GzTooltip.align}"
        @mouseleave="${GzTooltip.hide}"
        part="button"
        class="${this.type} ${this.color} ${this.theme}"
        role="button"
        ?disabled="${this.disabled}"
      >
        ${this.renderButtonContent()}
      </button>
    `;
  }

  setLoadingState(isLoading: boolean): void {
    this.showSpinner = isLoading;
    this.disabled = isLoading;
  }

  private handleMouseEnter(event: MouseEvent) {
    const element = event.target as HTMLElement;
    const textContent = element.querySelector('slot')?.assignedNodes()[0].textContent;

    if (!textContent || !isElementOverflowed(element, OverflowDirection.HORIZONTAL)) {
      return;
    }

    GzTooltip.show(textContent, event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-button': GzButtonWebComponent;
  }
}
